import { useMemo } from 'react';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import {
  boschBlue,
  boschGray,
} from '../../../modules/theme/boschColors';
import { useApiData } from './useApiData';
import { getJsonPath, toDisplayPrimitive } from './jsonPath';
import { ApiStatusBar } from './ApiStatusBar';

const useStyles = makeStyles(theme => ({
  scroll: {
    maxHeight: 480,
    overflow: 'auto',
  },
  headerCell: {
    backgroundColor: boschGray[95],
    fontWeight: 700,
    fontSize: '0.7rem',
    color: boschGray[20],
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderBottom: `1px solid ${boschGray[85]}`,
  },
  bodyCell: {
    fontSize: '0.82rem',
    color: boschGray[20],
    borderBottom: `1px solid ${boschGray[95]}`,
    /* Long values wrap rather than blowing out the table width.
     * Particularly useful for URL columns. */
    wordBreak: 'break-word',
    maxWidth: 360,
  },
  zebra: {
    '&:nth-of-type(even)': {
      backgroundColor: boschGray[95],
    },
    '&:hover': {
      backgroundColor: boschBlue[95],
    },
  },
  empty: {
    padding: theme.spacing(2),
    color: boschGray[40],
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
}));

export type ApiTableColumn = {
  /** Dot path applied to each ROW (not to the root). E.g. row
   *  `{user:{name:"Ana"}}` with key `user.name` shows "Ana". */
  key: string;
  /** Header text. Falls back to `key` when omitted. */
  label?: string;
};

export type ApiTableProps = {
  title?: string;
  url?: string;
  inlineJson?: string;
  /** Path to the ARRAY of rows. Most APIs nest data, e.g.
   *  `data.items` for a paginated list. */
  path?: string;
  columns: ApiTableColumn[];
  refreshSeconds?: number;
  /** Hard cap on rendered rows. Default 100 — anything more is a
   *  smell that the API needs paging on the backend. */
  maxRows?: number;
};

/**
 * Generic JSON-array → HTML table widget.
 *
 * The flexible bit: each column is a `{ key, label }` pair where
 * `key` is a dot-path resolved against the row. That means the
 * admin can render a nested shape like:
 *
 *   [
 *     {"user": {"name": "Ana", "email": "a@x"}, "score": 92},
 *     ...
 *   ]
 *
 * with columns `user.name`, `user.email`, `score` directly — no
 * server-side flattening needed.
 *
 * Why we don't use Backstage's <Table> from core-components: that
 * component is opinionated about column types (sortable, filterable,
 * date, ...) and bringing it here would force the admin to pick
 * column types up-front via the scaffolder UI. For a flexible "show
 * me any JSON array" widget, a plain MUI Table with dot-path keys
 * is friction-free and reads identically.
 */
export const ApiTable = ({
  title,
  url,
  inlineJson,
  path,
  columns,
  refreshSeconds,
  maxRows = 100,
}: ApiTableProps) => {
  const classes = useStyles();
  const { data, loading, error, lastUpdatedAt, refetch } = useApiData({
    url,
    inlineJson,
    path,
    refreshSeconds,
  });

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.slice(0, maxRows);
  }, [data, maxRows]);

  const sanitisedColumns = useMemo(
    () => columns.filter(c => c && typeof c.key === 'string' && c.key),
    [columns],
  );

  return (
    <InfoCard title={title ?? 'Data'} noPadding>
      {sanitisedColumns.length === 0 ? (
        <Typography className={classes.empty}>
          No columns configured. Add at least one column with a JSON path
          (e.g. <code>name</code> or <code>user.email</code>).
        </Typography>
      ) : !url && !inlineJson ? (
        <Typography className={classes.empty}>
          Provide a URL or inline JSON to populate this table.
        </Typography>
      ) : data === undefined && !loading && !error ? (
        <Typography className={classes.empty}>
          No data at path <code>{path || '(root)'}</code>.
        </Typography>
      ) : !Array.isArray(data) && data !== undefined ? (
        <Typography className={classes.empty}>
          Path <code>{path || '(root)'}</code> resolved to a {typeof data},
          not an array. Adjust the path so it points at the row list.
        </Typography>
      ) : (
        <TableContainer className={classes.scroll}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {sanitisedColumns.map(col => (
                  <TableCell key={col.key} className={classes.headerCell}>
                    {col.label || col.key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx} className={classes.zebra}>
                  {sanitisedColumns.map(col => (
                    <TableCell
                      key={col.key}
                      className={classes.bodyCell}
                      title={toDisplayPrimitive(getJsonPath(row, col.key))}
                    >
                      {toDisplayPrimitive(getJsonPath(row, col.key))}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {rows.length === 0 && Array.isArray(data) && (
                <TableRow>
                  <TableCell
                    colSpan={sanitisedColumns.length}
                    className={classes.bodyCell}
                    style={{ textAlign: 'center', fontStyle: 'italic' }}
                  >
                    No rows returned.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ApiStatusBar
        loading={loading}
        error={error}
        lastUpdatedAt={lastUpdatedAt}
        onRefresh={url ? refetch : undefined}
        modeLabel={url ? 'Live API' : 'Inline JSON'}
      />
    </InfoCard>
  );
};
